import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

/** Room a dropdown needs before it is worth opening downwards at all. */
const MIN_ROOM_PX = 140;
/** Never taller than this, however much room there is. */
const MAX_HEIGHT_PX = 260;
const GAP_PX = 2;

/**
 * Position a dropdown against its input so that nothing can clip it.
 *
 * An absolutely positioned list is clipped by the first ancestor that scrolls,
 * and every detail pane in this application scrolls. Worse, near the bottom of a
 * short window the list opened downwards into nothing — which is exactly where
 * the "add a line" forms sit, at the end of a long panel.
 *
 * `position: fixed` takes the list out of every scroll container there is, and
 * measuring the room left underneath decides whether it opens down or up. The
 * width is copied from the input so the two still read as one control.
 */
export function useAnchoredList(
  anchor: RefObject<HTMLElement | null>,
  open: boolean,
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ display: 'none' });

  // Layout effect, not effect: this runs before paint, so the list never shows
  // for one frame in the wrong place.
  useLayoutEffect(() => {
    if (!open) {
      setStyle({ display: 'none' });
      return;
    }

    const place = () => {
      const element = anchor.current;
      if (!element) return;

      const box = element.getBoundingClientRect();
      const below = window.innerHeight - box.bottom - GAP_PX;
      const above = box.top - GAP_PX;
      // Downwards unless it plainly does not fit and there is more room up.
      const upwards = below < MIN_ROOM_PX && above > below;
      const room = Math.max(MIN_ROOM_PX, upwards ? above : below);

      setStyle({
        position: 'fixed',
        left: box.left,
        width: Math.max(box.width, 240),
        maxHeight: Math.min(MAX_HEIGHT_PX, room),
        ...(upwards
          ? { bottom: window.innerHeight - box.top + GAP_PX }
          : { top: box.bottom + GAP_PX }),
      });
    };

    place();
    // Fixed positioning does not follow the page, so anything that moves the
    // input has to re-measure. Capture phase: the scroll that matters is
    // usually a pane's, not the window's.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor, open]);

  return style;
}
